import { useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';

/* ── 浅色工业风色值 ── */
const ORANGE = 'oklch(0.66 0.19 42)';
const CARD_BG = '#FFFFFF';
const BORDER = '#D0D5E2';
const MUTED_TEXT = '#5A6780';
const DEEP_BG = '#F0F2F5';
const SECTION_BG = '#ECEEF2';

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } } };
const stagger = { show: { transition: { staggerChildren: 0.07 } } };

/* ── 技术参数数据 ── */
interface TechParam {
  test: string;
  standard: string;
  value: string;
  note: string;
}

interface ProductSpec {
  id: string;
  code: string;
  name: string;
  nameEn: string;
  tagline: string;
  color: string;
  route: string;
  grades: { code: string; name: string }[];
  params: TechParam[];
  specs: { label: string; value: string }[];
  standards: string[];
}

const PRODUCTS: ProductSpec[] = [
  {
    id: 'fkm',
    code: 'FKM',
    name: 'FKM 氟橡胶板',
    nameEn: 'Fluorocarbon Rubber Sheet',
    tagline: '耐强腐蚀 · 超高温稳定 · 化工石化首选',
    color: '#e8640a',
    route: ROUTE_PATHS.PRODUCT_FKM,
    grades: [
      { code: 'FKM-S', name: '标准型' },
      { code: 'FKM-HT', name: '高温型' },
    ],
    params: [
      { test: '拉伸强度', standard: 'ISO 37 / ASTM D412', value: '≥10.0 MPa（标准型）/ ≥12.0 MPa（高温型）', note: '实验室实测值' },
      { test: '断裂伸长率', standard: 'ISO 37 / ASTM D412', value: '≥150%（标准型）/ ≥120%（高温型）', note: '标准配方' },
      { test: '硬度', standard: 'ISO 48 / ASTM D2240', value: '70±5 Shore A', note: '可定制 60A–90A' },
      { test: '比重', standard: 'ISO 2781', value: '1.95 g/cm³', note: '—' },
      { test: '工作温度范围', standard: '—', value: '-30°C ~ +220°C（标准）/ -20°C ~ +250°C（高温）', note: '特制配方可至 +250°C' },
      { test: '压缩永久变形', standard: 'ISO 815 / ASTM D395', value: '≤30%', note: '70°C × 22h' },
      { test: '耐油体积变化', standard: 'ISO 1817 / ASTM D471', value: '<25%', note: 'ASTM 3#油，70°C × 70h' },
      { test: '阻燃性', standard: '—', value: '自熄', note: '固有阻燃，无需添加剂' },
    ],
    specs: [
      { label: '颜色', value: '黑色 / 棕色 / 绿色' },
      { label: '厚度范围', value: '1.5 mm ~ 20.0 mm' },
      { label: '幅宽', value: '0.91 m ~ 2.0 m（标准）' },
      { label: '定制', value: '可按客户要求裁切' },
    ],
    standards: ['ISO 37', 'ISO 48', 'ISO 815', 'ISO 1817', 'ISO 2781', 'ASTM D412', 'ASTM D2240', 'ASTM D395', 'ASTM D471'],
  },
  {
    id: 'epdm',
    code: 'EPDM',
    name: 'EPDM 三元乙丙橡胶板',
    nameEn: 'EPDM Rubber Sheet',
    tagline: '卓越耐候 · 优异绝缘 · 户外建筑专用',
    color: '#2563eb',
    route: ROUTE_PATHS.PRODUCT_EPDM,
    grades: [
      { code: 'EPDM-S', name: '标准型' },
      { code: 'EPDM-I', name: '绝缘型' },
    ],
    params: [
      { test: '拉伸强度', standard: 'ISO 37 / ASTM D412', value: '≥8.0 MPa（标准型）/ ≥10.0 MPa（绝缘型）', note: '实验室实测值' },
      { test: '断裂伸长率', standard: 'ISO 37 / ASTM D412', value: '≥300%（标准型）/ ≥250%（绝缘型）', note: '标准配方' },
      { test: '硬度', standard: 'ISO 48 / ASTM D2240', value: '65±5 Shore A（标准型）/ 70±5 Shore A（绝缘型）', note: '可定制 50A–80A' },
      { test: '工作温度范围', standard: '—', value: '-40°C ~ +150°C（标准型）/ -40°C ~ +120°C（绝缘型）', note: '—' },
      { test: '耐臭氧性', standard: 'ISO 1431', value: '无龟裂', note: '50pphm，40°C，72h' },
      { test: '介电强度', standard: 'IEC 60243', value: '≥20 kV/mm（绝缘型）', note: '高压绝缘专用指标' },
      { test: '压缩永久变形', standard: 'ISO 815', value: '≤35%', note: '70°C × 22h' },
      { test: '比重', standard: 'ISO 2781', value: '1.20–1.35 g/cm³', note: '—' },
    ],
    specs: [
      { label: '颜色', value: '黑色 / 灰色 / 彩色' },
      { label: '厚度范围', value: '1.0 mm ~ 20.0 mm' },
      { label: '幅宽', value: '0.91 m ~ 2.0 m（标准）' },
      { label: '定制', value: '可按客户要求裁切' },
    ],
    standards: ['ISO 37', 'ISO 48', 'ISO 815', 'ISO 1431', 'ISO 2781', 'IEC 60243', 'ASTM D412', 'ASTM D2240'],
  },
  {
    id: 'nbr',
    code: 'NBR',
    name: 'NBR 丁腈橡胶板',
    nameEn: 'Nitrile Rubber Sheet',
    tagline: '优异耐油 · 高机械强度 · 液压系统标配',
    color: '#16a34a',
    route: ROUTE_PATHS.PRODUCT_NBR,
    grades: [
      { code: 'NBR-S', name: '标准型' },
      { code: 'NBR-HS', name: '高强型' },
    ],
    params: [
      { test: '拉伸强度', standard: 'ISO 37 / ASTM D412', value: '≥12.0 MPa（标准型）/ ≥15.0 MPa（高强型）', note: '实验室实测值' },
      { test: '断裂伸长率', standard: 'ISO 37 / ASTM D412', value: '≥350%（标准型）/ ≥300%（高强型）', note: '标准配方' },
      { test: '硬度', standard: 'ISO 48 / ASTM D2240', value: '70±5 Shore A（标准型）/ 75±5 Shore A（高强型）', note: '可定制 60A–80A' },
      { test: '工作温度范围', standard: '—', value: '-30°C ~ +100°C（标准型）/ -25°C ~ +90°C（高强型）', note: '—' },
      { test: '耐油体积变化（ASTM 3#）', standard: 'ISO 1817 / ASTM D471', value: '<20%', note: '70°C × 70h' },
      { test: '压缩永久变形', standard: 'ISO 815 / ASTM D395', value: '≤30%', note: '70°C × 22h' },
      { test: '比重', standard: 'ISO 2781', value: '1.30–1.45 g/cm³', note: '—' },
      { test: '耐磨性（DIN 53516）', standard: 'DIN 53516', value: '≤180 mm³', note: '标准磨耗量' },
    ],
    specs: [
      { label: '颜色', value: '黑色' },
      { label: '厚度范围', value: '1.0 mm ~ 20.0 mm' },
      { label: '幅宽', value: '0.91 m ~ 2.0 m（标准）' },
      { label: '定制', value: '可按客户要求裁切' },
    ],
    standards: ['ISO 37', 'ISO 48', 'ISO 815', 'ISO 1817', 'ISO 2781', 'DIN 53516', 'ASTM D412', 'ASTM D2240', 'ASTM D471'],
  },
  {
    id: 'nr',
    code: 'NR',
    name: 'NR 天然橡胶板（高耐磨配方）',
    nameEn: 'Natural Rubber Sheet — Wear Resistant',
    tagline: '极高弹性 · 超强耐磨 · 矿山输送首选',
    color: '#ca8a04',
    route: ROUTE_PATHS.PRODUCT_NR,
    grades: [
      { code: 'NR-S', name: '标准型' },
      { code: 'NR-WR', name: '耐磨型' },
    ],
    params: [
      { test: '拉伸强度', standard: 'ISO 37 / ASTM D412', value: '≥18.0 MPa（标准型）/ ≥20.0 MPa（耐磨型）', note: '实验室实测值' },
      { test: '断裂伸长率', standard: 'ISO 37 / ASTM D412', value: '≥650%（标准型）/ ≥450%（耐磨型）', note: '高耐磨配方实测' },
      { test: '硬度', standard: 'ISO 48 / ASTM D2240', value: '60±5 Shore A（标准型）/ 65±5 Shore A（耐磨型）', note: '可定制 50A–70A' },
      { test: '工作温度范围', standard: '—', value: '-50°C ~ +80°C（标准型）/ -40°C ~ +70°C（耐磨型）', note: '—' },
      { test: '耐磨性（DIN 53516）', standard: 'DIN 53516', value: '≤100 mm³（耐磨型）', note: '优于通用配方' },
      { test: '压缩永久变形', standard: 'ISO 815', value: '≤25%', note: '23°C × 22h' },
      { test: '回弹率', standard: 'ISO 4662', value: '≥60%', note: '室温测试' },
      { test: '比重', standard: 'ISO 2781', value: '1.15–1.25 g/cm³', note: '—' },
    ],
    specs: [
      { label: '颜色', value: '黑色 / 米色' },
      { label: '厚度范围', value: '2.0 mm ~ 20.0 mm' },
      { label: '幅宽', value: '0.91 m ~ 2.0 m（标准）' },
      { label: '定制', value: '可按客户要求裁切' },
    ],
    standards: ['ISO 37', 'ISO 48', 'ISO 815', 'ISO 4662', 'ISO 2781', 'DIN 53516', 'ASTM D412', 'ASTM D2240'],
  },
  {
    id: 'esd',
    code: 'ESD',
    name: 'ESD 防静电橡胶板',
    nameEn: 'Electrostatic Discharge Protection Rubber Sheet',
    tagline: '防静电 · 导电可控 · 电子半导体防爆专用',
    color: '#7c3aed',
    route: ROUTE_PATHS.PRODUCT_ESD,
    grades: [
      { code: 'ESD-S', name: '标准型（静电耗散）' },
      { code: 'ESD-C', name: '导电型' },
    ],
    params: [
      { test: '表面电阻（标准型）', standard: 'IEC 61340 / ANSI ESD S20.20', value: '10⁶ ~ 10⁹ Ω', note: '静电耗散级' },
      { test: '表面电阻（导电型）', standard: 'IEC 61340 / ANSI ESD S20.20', value: '10³ ~ 10⁶ Ω', note: '导电级' },
      { test: '拉伸强度', standard: 'ISO 37 / ASTM D412', value: '≥6.0 MPa（标准型）/ ≥5.0 MPa（导电型）', note: '实验室实测值' },
      { test: '断裂伸长率', standard: 'ISO 37 / ASTM D412', value: '≥200%（标准型）/ ≥180%（导电型）', note: '—' },
      { test: '硬度', standard: 'ISO 48 / ASTM D2240', value: '65±5 Shore A', note: '可定制 60A–75A' },
      { test: '工作温度范围', standard: '—', value: '-20°C ~ +70°C（标准型）/ -20°C ~ +60°C（导电型）', note: '—' },
      { test: '压缩永久变形', standard: 'ISO 815', value: '≤35%', note: '70°C × 22h' },
      { test: '比重', standard: 'ISO 2781', value: '1.40–1.60 g/cm³', note: '—' },
    ],
    specs: [
      { label: '颜色', value: '黑色 / 绿色' },
      { label: '厚度范围', value: '2.0 mm ~ 10.0 mm' },
      { label: '幅宽', value: '0.91 m ~ 1.5 m（标准）' },
      { label: '定制', value: '可按客户要求裁切' },
    ],
    standards: ['IEC 61340', 'ANSI/ESD S20.20', 'GB/T 11287', 'ISO 37', 'ISO 48', 'ISO 815', 'ASTM D412'],
  },
  {
    id: 'cr',
    code: 'CR',
    name: 'CR 氯丁橡胶板',
    nameEn: 'Chloroprene (Neoprene) Rubber Sheet',
    tagline: '固有阻燃 · 耐海水腐蚀 · 船舶建筑安全材料',
    color: '#0891b2',
    route: ROUTE_PATHS.PRODUCT_CR,
    grades: [
      { code: 'CR-S', name: '标准型' },
      { code: 'CR-M', name: '船用型' },
    ],
    params: [
      { test: '拉伸强度', standard: 'ISO 37 / ASTM D412', value: '≥10.0 MPa（标准型）/ ≥12.0 MPa（船用型）', note: '实验室实测值' },
      { test: '断裂伸长率', standard: 'ISO 37 / ASTM D412', value: '≥300%（标准型）/ ≥280%（船用型）', note: '—' },
      { test: '硬度', standard: 'ISO 48 / ASTM D2240', value: '65±5 Shore A（标准型）/ 70±5 Shore A（船用型）', note: '可定制 55A–75A' },
      { test: '工作温度范围', standard: '—', value: '-35°C ~ +100°C（标准型）/ -30°C ~ +90°C（船用型）', note: '—' },
      { test: '阻燃性', standard: 'UL 94 V-0 / ISO 4589', value: 'V-0 级（自熄）', note: '固有阻燃，无需添加剂' },
      { test: '耐海水浸泡体积变化', standard: 'ISO 1817', value: '<8%', note: '海水，23°C × 168h' },
      { test: '压缩永久变形', standard: 'ISO 815', value: '≤35%', note: '70°C × 22h' },
      { test: '比重', standard: 'ISO 2781', value: '1.40–1.60 g/cm³', note: '—' },
    ],
    specs: [
      { label: '颜色', value: '黑色' },
      { label: '厚度范围', value: '1.0 mm ~ 20.0 mm' },
      { label: '幅宽', value: '0.91 m ~ 2.0 m（标准）' },
      { label: '定制', value: '可按客户要求裁切' },
    ],
    standards: ['ISO 37', 'ISO 48', 'ISO 815', 'ISO 1817', 'ISO 4589', 'UL 94', 'ASTM D412', 'ASTM D2240'],
  },
];

export default function TechSpecs() {
  const location = useLocation();

  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  // 根据 URL hash 自动滚动到对应产品区域
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => scrollToSection(hash), 100);
    }
  }, [location.hash, scrollToSection]);

  return (
    <div className="bg-background text-foreground min-h-screen">
      {/* 面包屑 */}
      <div className="border-b border-border" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-xs" style={{ color: MUTED_TEXT }}>
            <Link to="/" className="hover:text-primary transition-colors">首页</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={ROUTE_PATHS.PRODUCTS} className="hover:text-primary transition-colors">产品系列</Link>
            <ChevronRight className="w-3 h-3" />
            <span style={{ color: ORANGE }}>技术参数详情</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 lg:py-20" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.div variants={fadeUp} className="mb-3">
              <Link
                to={ROUTE_PATHS.PRODUCTS}
                className="inline-flex items-center gap-2 text-sm transition-colors"
                style={{ color: MUTED_TEXT }}
              >
                <ArrowLeft className="w-4 h-4" />
                返回产品系列
              </Link>
            </motion.div>
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
              <div className="w-10 h-0.5" style={{ background: ORANGE }} />
              <span className="font-mono text-xs font-bold tracking-widest uppercase" style={{ color: ORANGE }}>
                TECHNICAL SPECIFICATIONS
              </span>
            </motion.div>
            <motion.h1 variants={fadeUp} className="text-4xl lg:text-5xl font-black mb-4">
              技术参数详情
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base max-w-2xl mb-8" style={{ color: MUTED_TEXT }}>
              所有数据均源自实验室测试报告与生产质量记录，依据 ISO / ASTM / IEC 国际标准体系。如需获取完整检测报告，请联系技术团队。
            </motion.p>
            {/* 快速跳转 */}
            <motion.div variants={fadeUp} className="flex flex-wrap gap-2">
              {PRODUCTS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => scrollToSection(p.id)}
                  className="px-4 py-2 text-xs font-bold rounded-sm font-mono tracking-wider transition-all hover:opacity-80"
                  style={{ background: CARD_BG, border: `1px solid ${BORDER}`, color: ORANGE }}
                >
                  {p.code}
                </button>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 各产品技术参数 */}
      {PRODUCTS.map((product, pi) => (
        <section
          key={product.id}
          id={product.id}
          className="py-16 scroll-mt-20"
          style={{ background: pi % 2 === 0 ? SECTION_BG : DEEP_BG }}
        >
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={stagger}
            >
              {/* 产品标题 */}
              <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-4 mb-8">
                <span
                  className="px-3 py-1 text-xs font-black tracking-[0.2em] uppercase rounded-sm font-mono"
                  style={{ background: 'oklch(0.66 0.19 42 / 0.12)', color: ORANGE, border: `1px solid oklch(0.66 0.19 42 / 0.4)` }}
                >
                  {product.code}
                </span>
                <div>
                  <h2 className="text-2xl lg:text-3xl font-black">{product.name}</h2>
                  <p className="text-xs font-mono tracking-wider mt-0.5" style={{ color: MUTED_TEXT }}>{product.nameEn}</p>
                </div>
                <Link
                  to={product.route}
                  className="ml-auto text-xs font-bold inline-flex items-center gap-1 transition-colors hover:opacity-80"
                  style={{ color: ORANGE }}
                >
                  查看产品详情 <ChevronRight className="w-3 h-3" />
                </Link>
              </motion.div>
              <motion.p variants={fadeUp} className="text-sm mb-8" style={{ color: MUTED_TEXT }}>{product.tagline}</motion.p>

              {/* 产品型号 */}
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3 mb-8">
                {product.grades.map((g) => (
                  <div
                    key={g.code}
                    className="px-4 py-2 rounded-sm text-sm"
                    style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                  >
                    <span className="font-mono font-bold text-xs" style={{ color: ORANGE }}>{g.code}</span>
                    <span className="ml-2 text-xs" style={{ color: MUTED_TEXT }}>{g.name}</span>
                  </div>
                ))}
              </motion.div>

              {/* 技术参数表 */}
              <motion.div variants={fadeUp} className="rounded overflow-hidden mb-8" style={{ border: `1px solid ${BORDER}` }}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                        {['测试项目', '参考标准', '技术指标', '备注'].map((h) => (
                          <th
                            key={h}
                            className="px-5 py-3.5 text-left text-xs font-black tracking-widest uppercase"
                            style={{ color: ORANGE }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {product.params.map((row, i) => (
                        <tr
                          key={row.test}
                          style={{
                            background: i % 2 === 0 ? CARD_BG : '#F0F2F5',
                            borderBottom: `1px solid ${BORDER}`,
                          }}
                        >
                          <td className="px-5 py-3.5 font-semibold text-sm">{row.test}</td>
                          <td className="px-5 py-3.5 font-mono text-xs" style={{ color: 'oklch(0.78 0.012 225)' }}>{row.standard}</td>
                          <td className="px-5 py-3.5 font-bold text-sm" style={{ color: ORANGE }}>{row.value}</td>
                          <td className="px-5 py-3.5 text-xs" style={{ color: MUTED_TEXT }}>{row.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>

              {/* 规格 + 标准 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 产品规格 */}
                <motion.div
                  variants={fadeUp}
                  className="rounded p-5"
                  style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                >
                  <h3 className="text-xs font-black tracking-widest uppercase mb-4" style={{ color: ORANGE }}>
                    产品规格
                  </h3>
                  {product.specs.map((s) => (
                    <div
                      key={s.label}
                      className="flex items-center gap-4 text-sm py-2.5"
                      style={{ borderBottom: `1px solid ${BORDER}` }}
                    >
                      <span className="w-24 flex-shrink-0 text-xs font-bold" style={{ color: MUTED_TEXT }}>{s.label}</span>
                      <span className="font-semibold">{s.value}</span>
                    </div>
                  ))}
                </motion.div>

                {/* 符合标准 */}
                <motion.div
                  variants={fadeUp}
                  className="rounded p-5"
                  style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                >
                  <h3 className="text-xs font-black tracking-widest uppercase mb-4" style={{ color: ORANGE }}>
                    符合标准
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {product.standards.map((s) => (
                      <span
                        key={s}
                        className="px-3 py-1 text-xs font-mono font-bold rounded-sm"
                        style={{ background: 'oklch(0.66 0.19 42 / 0.10)', color: 'oklch(0.78 0.12 42)', border: `1px solid oklch(0.66 0.19 42 / 0.25)` }}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs mt-4" style={{ color: MUTED_TEXT }}>
                    如需完整检测报告或第三方认证文件，请联系我们的技术团队。
                  </p>
                </motion.div>
              </div>

              {/* CTA */}
              <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-3">
                <Link
                  to={ROUTE_PATHS.CONTACT}
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-sm transition-all"
                  style={{ background: ORANGE, color: '#FFFFFF' }}
                >
                  获取 {product.code} 检测报告
                </Link>
                <Link
                  to={product.route}
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-sm transition-all"
                  style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
                >
                  {product.code} 产品详情 <ChevronRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* 底部说明 */}
      <section className="py-12" style={{ background: DEEP_BG, borderTop: `1px solid ${BORDER}` }}>
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm mb-2" style={{ color: MUTED_TEXT }}>
            以上数据均为实验室测试典型值，不作为产品最终规格保证。实际性能可能因配方、加工条件及使用环境而有所差异。
          </p>
          <p className="text-xs" style={{ color: '#5A6780' }}>
            如需特定批次检测报告、第三方认证文件或定制规格，请通过
            <Link to={ROUTE_PATHS.CONTACT} className="underline hover:opacity-80 mx-1" style={{ color: ORANGE }}>联系我们</Link>
            页面提交需求。
          </p>
        </div>
      </section>
    </div>
  );
}
